import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import './PdfViewer.scss';
import {InDocumentSearchProps} from './PdfViewer.types';
import {highlightElement, scrollToElementFromContent, scrollToElementFromHighlight} from './ViewerUtils';
import {useDispatch} from 'react-redux';
import {ReactZoomPanPinchRef, TransformComponent, TransformWrapper} from 'react-zoom-pan-pinch';
import {
    useSelectedDocContext,
    useStateCurrentPageNumber,
    useStateCurrentPageRotation,
    useStateDocumentHighlights,
    useStateDocumentQueryHighlights,
    useStateDoResetTransform,
    useStateHighlightId,
    useStateNavigationRequest,
    useStateScrollTarget,
    useStateShowAnnotations,
} from '../../search/search-hooks';
import {
    resetTransformAction,
    setCurrentPageNumberAction,
    setCurrentPageRotationAction,
    setDocumentAllPagesRotation,
    setDocumentQueryHighlights,
    setDocumentSelectedTags,
    setHighlightIdAction,
    setSelectedDocumentNumberOfPages,
} from '../../search/duck/search-actions';
import {ClickableElement} from '../../search/search-results/search-result-details/content/clickable-section-list/clickableSectionList';
import {computePageRotation} from '../../utils/utils';
import {PagesController} from '../pages-controller/PagesController';
import {Spinner} from '../spinner/spinner';
import {useSpotlightTagTypes} from '../../config/config-hooks';
import {usePdfDocument} from './usePdfDocument';
import {PdfPage} from './PdfPage';
import {HighlightOverlay} from './HighlightOverlay';
import {PageViewBox} from './spanMath';
import {buildSpotlightTagHighlights} from './tagHighlights';
import {buildQueryHighlights} from './queryHighlights';

interface PdfViewerProps {
    file?: Blob;
    isLoading: boolean;
    onInDocumentSearchPropsChange: (inDocumentSearchProps: InDocumentSearchProps) => void;
}

// How long to wait for a highlight box to appear. It is missing while the PDF is still loading, for example.
const HIGHLIGHT_WAIT_MS = 5000;

export const PdfViewer = memo(({file, isLoading, onInDocumentSearchPropsChange}: PdfViewerProps) => {
    const {pdf, error} = usePdfDocument(file);
    const selectedDocument = useSelectedDocContext();
    const spotlightTagTypes = useSpotlightTagTypes();
    const queryHighlights = useStateDocumentQueryHighlights();
    const querySpans = useStateDocumentHighlights()?.querySpans;
    const highlightId = useStateHighlightId(); // tag or query highlight
    const navigationRequest = useStateNavigationRequest(); // changes on every jump, see handleScrolling
    const pageNumber = useStateCurrentPageNumber();
    const pageRotation = useStateCurrentPageRotation();
    const showAnnotations = useStateShowAnnotations();
    const doResetTransform = useStateDoResetTransform();
    const scrollTarget: ClickableElement | null = useStateScrollTarget();
    const refPreviewPageScroll: React.RefObject<any> = useRef(null);
    const transformRef = useRef<ReactZoomPanPinchRef>(null);
    const dispatch = useDispatch();
    const [width, setWidth] = useState(800);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
    const [percentage, setPercentage] = useState<number>(100);
    const [scale, setScale] = useState<number>(1);
    const [pageView, setPageView] = useState<PageViewBox>({w: 0, h: 0, x: 0, y: 0});

    const tagHighlights = useMemo(
        () => buildSpotlightTagHighlights(selectedDocument, spotlightTagTypes),
        [selectedDocument, spotlightTagTypes],
    );

    // The Spotlight panel lists these tags. They share ids with the overlay boxes, so a click on a row finds its box.
    useEffect(() => {
        dispatch(setDocumentSelectedTags(tagHighlights));
    }, [dispatch, tagHighlights]);

    // Latest values for the code below, without making the effects re-run when they change.
    const queryHighlightsRef = useRef(queryHighlights);
    queryHighlightsRef.current = queryHighlights;
    const pageNumberRef = useRef(pageNumber);
    pageNumberRef.current = pageNumber;
    const highlightIdRef = useRef(highlightId);
    highlightIdRef.current = highlightId;

    // handleScrolling is memoised and also runs as PdfPage's onRendered, so without refs it would see the
    // values of the render that created it, for example a Spotlight tab that has been closed since.
    const showAnnotationsRef = useRef(showAnnotations);
    showAnnotationsRef.current = showAnnotations;
    const tagHighlightsRef = useRef(tagHighlights);
    tagHighlightsRef.current = tagHighlights;
    // Same for the rotation. Rotating does not change handleScrolling's dependencies.
    const pageRotationRef = useRef(pageRotation);
    pageRotationRef.current = pageRotation;

    // Runs each time a PDF is loaded. Stores each page's rotation and the page count, and resets an out-of-range
    // page to 1.
    useEffect(() => {
        if (!pdf) {
            return;
        }
        let cancelled = false;
        (async () => {
            const pageRotations: number[] = [];
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                if (cancelled) {
                    return;
                }
                pageRotations[i - 1] = page.rotate;
            }
            if (cancelled) {
                return;
            }
            dispatch(setDocumentAllPagesRotation(pageRotations));
            dispatch(setSelectedDocumentNumberOfPages(pdf.numPages));
            if (pageNumberRef.current < 1 || pageNumberRef.current > pdf.numPages) {
                dispatch(setCurrentPageNumberAction(1));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [pdf, dispatch]);

    // Runs whenever the search hits change. Builds the query boxes and jumps to the first hit.
    useEffect(() => {
        const newQueryHighlights = buildQueryHighlights(querySpans);
        setHighlightedIndex(0);
        dispatch(setDocumentQueryHighlights(newQueryHighlights));

        const firstHit = newQueryHighlights[0];
        if (firstHit) {
            dispatch(setCurrentPageNumberAction(firstHit.page));
            // true, because every search's first hit is query#0. The id may stay the same, and this still scrolls.
            dispatch(setHighlightIdAction(firstHit.id, true));
        } else if (queryHighlightsRef.current.some((q) => q.id === highlightIdRef.current)) {
            // Clear the previous search's selected hit, but not a selected tag.
            dispatch(setHighlightIdAction(undefined));
        }
    }, [querySpans, dispatch]);

    const onQueryHighlightIndexChange = useCallback(
        (queryHighlightIndex: number) => {
            const queryHighlight = queryHighlights[queryHighlightIndex];
            if (queryHighlight) {
                setHighlightedIndex(queryHighlightIndex);
                dispatch(setCurrentPageNumberAction(queryHighlight.page));
                dispatch(setHighlightIdAction(queryHighlight.id, true));
            }
        },
        [dispatch, queryHighlights],
    );

    useEffect(() => {
        const data: InDocumentSearchProps = {
            numHighlights: queryHighlights.length,
            highlightedIndex: highlightedIndex,
            onHighlightIndexChange: onQueryHighlightIndexChange,
        };
        onInDocumentSearchPropsChange(data);
    }, [highlightedIndex, onQueryHighlightIndexChange, onInDocumentSearchPropsChange, queryHighlights.length]);

    // Track the container width, so the page can fit it.
    useEffect(() => {
        const stage = document.getElementById('hibuPdfPreview');
        if (!stage) {
            return;
        }
        const resizeObserver = new ResizeObserver((event) => {
            // rAF avoids the "ResizeObserver loop limit exceeded" error
            window.requestAnimationFrame(() => {
                const newWidth = event[0].contentBoxSize[0].inlineSize;
                setWidth((prev) => (newWidth && newWidth !== prev ? newWidth : prev));
            });
        });
        resizeObserver.observe(stage);
        return () => resizeObserver.disconnect();
    }, []);

    // Shows a grab cursor while Ctrl or Alt is held, and a grabbing cursor while dragging.
    useEffect(() => {
        const toggle = (add: boolean, cls: string) => {
            const el: HTMLDivElement | undefined = refPreviewPageScroll.current ?? undefined;
            el?.classList[add ? 'add' : 'remove'](cls);
        };
        const onKeyDown = (e: KeyboardEvent) => (e.key === 'Alt' || e.key === 'Control') && toggle(true, 'key-pressed');
        const onKeyUp = (e: KeyboardEvent) => (e.key === 'Alt' || e.key === 'Control') && toggle(false, 'key-pressed');
        const onMouseDown = () => toggle(true, 'mouse-down');
        const onMouseUp = () => toggle(false, 'mouse-down');
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        window.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
            window.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, []);

    // A one-time request, so set it back to false once handled. If it stayed true, the next request would
    // not change the value, and this effect would not run for it.
    useEffect(() => {
        if (doResetTransform) {
            // Instantly, not animated. The scroll below measures positions right away, and an animated
            // reset would still include the old pan.
            transformRef.current?.resetTransform(0);
            dispatch(resetTransformAction(false));
        }
    }, [doResetTransform, dispatch]);

    // Clear a selected tag when the Spotlight tab closes. Otherwise, after the tab is reopened, the next zoom or
    // rotation can jump back to that old tag. A selected query hit stays, because query boxes show on any tab.
    useEffect(() => {
        if (
            !showAnnotations &&
            highlightId &&
            tagHighlights.some((tag) => tag.id === highlightId) &&
            !queryHighlights.some((queryHighlight) => queryHighlight.id === highlightId)
        ) {
            dispatch(setHighlightIdAction(undefined));
        }
    }, [showAnnotations, highlightId, tagHighlights, queryHighlights, dispatch]);

    const scrollToContent = () => {
        if (scrollTarget !== null) {
            scrollToElementFromContent(scrollTarget, refPreviewPageScroll, pageRotationRef.current ?? 0);
        }
    };

    /**
     * Waits until the highlight box is on the page, for at most `HIGHLIGHT_WAIT_MS`, then gives up with `null`.
     * Without the limit, every zoom, rotation, page change or resize could leave another wait running forever.
     * It counts time, not frames, so a 120 Hz screen does not shorten it.
     */
    const isElementLoaded = async (selector: string): Promise<Element | null> => {
        const deadline = performance.now() + HIGHLIGHT_WAIT_MS;
        let element = document.querySelector(selector);
        while (element === null && performance.now() < deadline) {
            await new Promise((resolve) => requestAnimationFrame(resolve));
            element = document.querySelector(selector);
        }
        return element;
    };

    const scrollToHighlight = () => {
        if (!highlightId) {
            return;
        }
        // The effect above clears a hidden tag one render late. If onRendered fires in between, stop here
        // instead of waiting the full timeout for a box that is not drawn.
        const isUndrawnTag =
            !showAnnotationsRef.current &&
            tagHighlightsRef.current.some((tag) => tag.id === highlightId) &&
            !queryHighlightsRef.current.some((queryHighlight) => queryHighlight.id === highlightId);
        if (isUndrawnTag) {
            return;
        }
        // Escaped because tag ids contain label text, which can include quotes or backslashes.
        isElementLoaded('[data-annotation-id=' + CSS.escape(highlightId) + ']').then((htmlElement) => {
            if (htmlElement) {
                scrollToElementFromHighlight(htmlElement, refPreviewPageScroll);
                highlightElement(htmlElement);
            }
        });
    };

    const handleScrolling = useCallback(() => {
        if (scrollTarget) {
            // handle jumps to toc elements and sections
            scrollToContent();
        } else if (highlightId) {
            // handle jumps to query highlights and tag highlights
            scrollToHighlight();
        }
        // navigationRequest is not used in the body. It is listed so that selecting the same target again
        // scrolls again.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollTarget, highlightId, navigationRequest]);

    // Scroll when the target changes or is selected again. Page changes scroll through onRendered.
    useEffect(() => {
        handleScrolling();
    }, [handleScrolling]);

    const onDims = useCallback((view: PageViewBox) => {
        setPageView((prev) =>
            prev.w === view.w && prev.h === view.h && prev.x === view.x && prev.y === view.y ? prev : view,
        );
    }, []);

    function handleZoomIn() {
        dispatch(resetTransformAction(false));
        const newScale = scale >= 1 ? scale + 0.25 : scale + 0.1;
        setScale(newScale);
        setPercentage(Math.floor(newScale * 100));
    }

    function handleZoomOut() {
        dispatch(resetTransformAction(false));
        if (scale >= 0.3) {
            const newScale = scale <= 1 ? scale - 0.1 : scale - 0.25;
            setScale(newScale);
            setPercentage(Math.floor(newScale * 100));
        }
    }

    function handleResetZoomAndPan() {
        setScale(1);
        setPercentage(100);
        setTimeout(() => {
            transformRef.current?.resetTransform(); // reset panning; setTimeout needed because of timing issue
        }, 0);
    }

    function handlePageRotation(rotation: number) {
        dispatch(setCurrentPageRotationAction(computePageRotation(pageRotation, rotation)));
        handleResetZoomAndPan();
    }

    return (
        <div id={'hibuPdfPreview'}>
            {isLoading ? (
                <Spinner/>
            ) : error ? (
                <div className={'pad-error'}>{error}</div>
            ) : !pdf ? (
                <Spinner/>
            ) : (
                <div id={'hibuPdfPreviewPageWrapper'}>
                    <TransformWrapper
                        ref={transformRef}
                        wheel={{disabled: true}}
                        doubleClick={{disabled: true}}
                        limitToBounds={false}
                        panning={{activationKeys: ['Control', 'Alt']}}
                    >
                        <div id={'hibuPdfPreviewPageScroll'} ref={refPreviewPageScroll}>
                            <div id={'hibuPdfPreviewPage'}>
                                <TransformComponent>
                                    <PdfPage
                                        pdf={pdf}
                                        pageNumber={pageNumber}
                                        scale={scale}
                                        width={width}
                                        rotate={pageRotation ?? 0}
                                        onDims={onDims}
                                        onRendered={handleScrolling}
                                    >
                                        <HighlightOverlay
                                            tagHighlights={tagHighlights}
                                            queryHighlights={queryHighlights}
                                            page={pageNumber}
                                            rotate={pageRotation ?? 0}
                                            pageView={pageView}
                                            showTags={showAnnotations}
                                        />
                                    </PdfPage>
                                </TransformComponent>
                            </div>
                        </div>
                        <PagesController
                            percentage={percentage}
                            handleZoomIn={handleZoomIn}
                            handleZoomOut={handleZoomOut}
                            handleResetZoomAndPan={handleResetZoomAndPan}
                            handlePageRotation={handlePageRotation}
                            showPagesButtons={true}
                        />
                    </TransformWrapper>
                </div>
            )}
        </div>
    );
});
